import { useState } from "react";
import { syncTiers } from "../../data/client";
import { matrixKeyFor } from "../../data/types";
import { Button, Display, Panel, Sprite } from "../../design/primitives";
import { art } from "../../design/sprites";
import { CreatorActions } from "./CreatorActions";
import { useStudio } from "./StudioContext";
import css from "./studio.module.css";

/**
 * Tiers & access - §11.2.
 *
 * Tier names, prices and patron counts are **read-only**, from Patreon (§11.4).
 * The blurb and the grant matrix are the only editable things on this screen,
 * and both are creator data rather than anything derived.
 */
export default function Tiers() {
  const { creator, update, replace } = useStudio();
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const resync = async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      const synced = await syncTiers();
      // The sync writes server-side, so this is already saved - `replace`
      // adopts it without marking the record dirty (D-31).
      replace(synced);
      setConfirmation(`SYNCED ${synced.tiers.length} TIERS FROM PATREON`);
    } catch (e) {
      // §13.1: show the last good sync and say the grants still apply. Losing
      // contact with Patreon does not change who can download what.
      setSyncError((e as Error).message);
    } finally {
      setSyncing(false);
    }
  };

  /** Every (assetType, slot) pair is a matrix row - the key is the pair (D-7). */
  const rows = creator.assetTypes
    .filter((t) => !t.retired)
    .flatMap((type) =>
      type.slots.map((slot) => ({
        key: matrixKeyFor(type.type, slot.id),
        label: `${type.label} — ${slot.label.replace(/^.*—\s*/, "")}`,
      }))
    );

  const toggle = (rowKey: string, tierId: string) => {
    const row = creator.matrix[rowKey] ?? {};
    update({
      ...creator,
      matrix: {
        ...creator.matrix,
        [rowKey]: { ...row, [tierId]: !row[tierId] },
      },
    });
  };

  const price = (cents: number) =>
    `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}/mo`;

  return (
    <>
      <CreatorActions />
      <div className={css.head}>
        <Display size={38} as="h1">TIERS & ACCESS</Display>
      </div>

      <div className={css.syncRow}>
        <span className={css.label}>
          {creator.tiersSyncedAt
            ? `LAST SYNCED ${new Date(creator.tiersSyncedAt).toLocaleString()}`
            : "NEVER SYNCED"}
        </span>
        <Button variant="secondary" size="rail" onClick={resync} disabled={syncing}>
          {syncing ? "SYNCING…" : "RE-SYNC FROM PATREON"}
        </Button>
        {confirmation && <span className={css.confirm}>{confirmation}</span>}
        {syncError && (
          <span className={css.refuse}>
            SYNC FAILED · SHOWING THE LAST GOOD SYNC · GRANTS STILL APPLY
          </span>
        )}
      </div>

      {creator.tiers.length === 0 ? (
        <Panel style={{ padding: "var(--space-32)", textAlign: "center" }}>
          <Sprite src={art.crt} size={104} />
          <Display size={21} as="p" style={{ margin: "var(--space-20) 0" }}>
            NO TIERS YET
          </Display>
          <p className={css.note}>
            Tiers come from Patreon and are never written here. Sync to pull
            them in, then the access matrix has columns.
          </p>
        </Panel>
      ) : (
        <>
          <div className={css.tierGrid}>
            {creator.tiers.map((tier) => (
              <Panel key={tier.id} className={css.tierCard}>
                <Sprite src={art.cassette} size={52} label={`${tier.name} tier`} />
                <span className={css.tierName}>{tier.name}</span>
                <span className={css.tierPrice}>{price(tier.amountCents)}</span>
                <textarea
                  className={css.tierBlurb}
                  rows={3}
                  placeholder="One sentence about this tier."
                  value={creator.tierBlurbs[tier.id] ?? ""}
                  onChange={(e) =>
                    update({
                      ...creator,
                      tierBlurbs: { ...creator.tierBlurbs, [tier.id]: e.target.value },
                    })
                  }
                />
                <div className={css.tierFooter}>
                  <span className={css.tierPatrons}>{tier.patrons} PATRONS</span>
                  <span className={css.tierTypes}>
                    {rows.filter((r) => creator.matrix[r.key]?.[tier.id]).length} OF{" "}
                    {rows.length} FILES
                  </span>
                </div>
              </Panel>
            ))}
          </div>

          <Display size={21} as="h2" style={{ letterSpacing: "var(--track-section-header)" }}>
            ACCESS MATRIX
          </Display>

          <div
            className={css.matrix}
            style={{ ["--tier-count" as string]: creator.tiers.length }}
          >
            <div className={css.matrixRow}>
              <span className={css.matrixHeadCell}>FILE</span>
              {creator.tiers.map((t) => (
                <span key={t.id} className={css.matrixHeadCell}>
                  {t.name.toUpperCase()}
                </span>
              ))}
            </div>
            {rows.map((row) => (
              <div key={row.key} className={css.matrixRow}>
                <span className={css.matrixRowLabel}>{row.label}</span>
                {creator.tiers.map((tier) => {
                  const granted = creator.matrix[row.key]?.[tier.id] === true;
                  return (
                    <button
                      key={tier.id}
                      type="button"
                      aria-pressed={granted}
                      aria-label={`${row.label} for ${tier.name}`}
                      className={`${css.cell} ${granted ? css.cellGranted : css.cellDenied}`}
                      onClick={() => toggle(row.key, tier.id)}
                    >
                      {granted ? "✓" : "—"}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <p className={css.note}>
            Access is enforced on the server on every download. This grid is the
            source of that decision, not a display of it.
          </p>
        </>
      )}
    </>
  );
}
